import { DropPhaseType } from "@anaconda/core/drop/types"

/**
 * Calculates available tokens for minting based on various constraints
 * @param maxMintPerWallet - Maximum tokens per wallet
 * @param phaseLimit - Maximum tokens that can be minted in this phase (maxClaimableSupply)
 * @param totalSupply - Total supply of the entire drop
 * @param mintedCount - Total number of tokens minted so far
 * @param userMintedCount - Number of tokens already minted by user
 * @param maxQuantity - Maximum tokens that can be minted in a single transaction
 * @param phaseType - Type of the current phase (Allowlist or Public)
 * @param userClaimLimitInfo - User's claim limit info for Allowlist phases
 * @returns Maximum number of tokens available for minting
 */
export function calculateAvailableToMint({
  maxMintPerWallet,
  phaseLimit,
  totalSupply,
  mintedCount,
  userMintedCount,
  maxQuantity,
  phaseType,
  userClaimLimitInfo,
}: {
  maxMintPerWallet?: number
  phaseLimit?: number
  totalSupply?: number
  mintedCount?: number
  userMintedCount: number
  maxQuantity: number
  phaseType?: string
  userClaimLimitInfo?: {
    remainingLimit: number
  } | null
}): number {
  // For Allowlist phases, use user's remaining limit
  if (phaseType === DropPhaseType.Allowlist && userClaimLimitInfo) {
    const remainingPhaseSupply =
      phaseLimit && mintedCount
        ? Math.max(0, phaseLimit - mintedCount)
        : Number.POSITIVE_INFINITY

    const remainingTotalSupply =
      totalSupply && mintedCount
        ? Math.max(0, totalSupply - mintedCount)
        : Number.POSITIVE_INFINITY

    return Math.min(
      userClaimLimitInfo.remainingLimit,
      remainingPhaseSupply,
      remainingTotalSupply,
      maxQuantity,
    )
  }

  // For Public phases, use wallet limit
  const walletLimit = maxMintPerWallet
    ? Math.max(0, maxMintPerWallet - userMintedCount)
    : maxQuantity

  const remainingPhaseSupply =
    phaseLimit && mintedCount
      ? Math.max(0, phaseLimit - mintedCount)
      : Number.POSITIVE_INFINITY

  const remainingTotalSupply =
    totalSupply && mintedCount
      ? Math.max(0, totalSupply - mintedCount)
      : Number.POSITIVE_INFINITY

  const remainingSupply = Math.min(remainingPhaseSupply, remainingTotalSupply)

  return Math.min(walletLimit, remainingSupply, maxQuantity)
}
